/** The Android renderer.
 *
 * Structure mirrors the app so an upstream diff maps onto a local diff:
 *   adapters/  track OUR api payload
 *   mappers/   line-by-line ports of *StateMapper.kt — track Android's logic
 *   items/     ports of item_*.xml + delegates — track Android's layouts
 *   tokens/    GENERATED from res/values/*.xml
 *
 * Drift is checked manually: `pnpm run renderer:drift`. See the spec's §10.
 */
import type { PreviewApiType } from '@/api/mobilePreview';

import colorsCss from './tokens/colors.css?inline';
import dimensCss from './tokens/dimens.css?inline';
import typographyCss from './tokens/typography.css?inline';
import robotoCss from '@fontsource/roboto/400.css?inline';
import robotoMediumCss from '@fontsource/roboto/500.css?inline';
import robotoBoldCss from '@fontsource/roboto/700.css?inline';

import provenance from './provenance.json';
import { ActivityScreen, activityScreenNotes } from './ActivityScreen';
import { ItineraryScreen, itineraryScreenNotes } from './ItineraryScreen';
import { TourScreen, tourScreenNotes } from './TourScreen';
import { AndroidShell } from './Shell';
import { MyTripsScreen, myTripsScreenNotes } from './MyTripsScreen';
import { androidScreens, MY_TRIPS_SCREEN } from './screens';
import { registerRenderer } from '../../rendererRegistry';
import type { Renderer, ScreenProps } from '../../types';

const SUPPORTED: PreviewApiType[] = ['activity', 'itinerary', 'tour_template'];

function Screen(props: ScreenProps & { screen?: string }) {
  if (props.apiType === 'tour_template' && props.screen === MY_TRIPS_SCREEN) return <MyTripsScreen {...props} />;
  switch (props.apiType) {
    case 'activity':
      return <ActivityScreen {...props} />;
    case 'itinerary':
      return <ItineraryScreen {...props} />;
    case 'tour_template':
      return <TourScreen {...props} />;
    default:
      return null;
  }
}

export const androidRenderer: Renderer = {
  id: 'android',
  label: 'Android',
  formFactors: ['phone'],
  supports: (apiType) => SUPPORTED.includes(apiType),
  Screen,
  Shell: AndroidShell,
  screens: androidScreens,
  provenance: {
    repo: provenance.repo,
    ref: provenance.ref,
    commit: provenance.commit,
    syncedAt: provenance.syncedAt,
  },
  // Roboto first: the @font-face rules must exist inside the frame document.
  styles: [robotoCss, robotoMediumCss, robotoBoldCss, colorsCss, dimensCss, typographyCss],
};

registerRenderer(androidRenderer);

export { activityScreenNotes, itineraryScreenNotes, myTripsScreenNotes, tourScreenNotes };
